import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { QorumLogo } from "@/components/brand/QorumLogo";
import { FeedbackProductModal, type FeedbackProductInfo } from "@/components/qorum/FeedbackProductModal";
import { socialMeta, pageJsonLd } from "@/lib/seo-meta";

export const Route = createFileRoute("/phase-pro")({
  head: () => ({
    meta: [
      { title: "La Phase Pro — Hōjin" },
      {
        name: "description",
        content:
          "Six livrables documentaires exploitables après la délibération du Conseil : mémo, pitch, business plan, white paper, étude de marché, plan financier.",
      },
      ...socialMeta({
        title: "La Phase Pro — Hōjin",
        description: "Six livrables documentaires exploitables après la délibération du Conseil : mémo, pitch, business plan, white paper, étude de marché, plan financier.",
        url: "https://hojin.ai/phase-pro",
        image: "memo",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/phase-pro" }],
    scripts: pageJsonLd({
      path: "/phase-pro",
      name: "La Phase Pro — Hōjin",
      description: "Six livrables documentaires exploitables après la délibération du Conseil : mémo, pitch, business plan, white paper, étude de marché, plan financier.",
      type: "WebPage",
      image: "memo",
      breadcrumb: [{ name: "La Phase Pro", path: "/phase-pro" }],
    }),
  }),
  component: PhaseProPage,
});

type Status = "available" | "soon";

interface Product {
  key: string;
  name: string;
  tagline: string;
  description: string;
  priceLabel: string;
  defaultPrice: number;
  status: Status;
  cta: { label: string; href?: string };
}

const PRODUCTS: Product[] = [
  {
    key: "memo",
    name: "Memo exécutif",
    tagline: "Synthèse 1-2 pages style cabinet conseil.",
    description:
      "Verdict + décisions critiques + risques + note de dissidence + sources. Markdown ou HTML, prêt à transmettre.",
    priceLabel: "Gratuit en bêta",
    defaultPrice: 29,
    status: "available",
    cta: { label: "Lancer une délibération →", href: "/" },
  },
  {
    key: "pitch_deck",
    name: "Pitch Deck",
    tagline: "10-12 slides pour présenter.",
    description:
      "Problème, solution, marché, traction, équipe, demande. Format PowerPoint + Google Slides + Keynote, à votre charte.",
    priceLabel: "Bientôt",
    defaultPrice: 49,
    status: "soon",
    cta: { label: "Donner mon avis →" },
  },
  {
    key: "business_plan",
    name: "Business Plan",
    tagline: "Document 30-40 pages investissable.",
    description:
      "Marché, modèle, roadmap, équipe, finances projetées sur 3 ans. Word et PDF, prêt série A.",
    priceLabel: "Bientôt",
    defaultPrice: 149,
    status: "soon",
    cta: { label: "Donner mon avis →" },
  },
  {
    key: "white_paper",
    name: "White Paper",
    tagline: "Étude de fond positionnante.",
    description:
      "Analyse approfondie d'un sujet (tech, marché, réglementation). Pour bâtir l'autorité d'un founder ou d'une équipe sur un domaine.",
    priceLabel: "Bientôt",
    defaultPrice: 199,
    status: "soon",
    cta: { label: "Donner mon avis →" },
  },
  {
    key: "market_study",
    name: "Étude de marché",
    tagline: "Cartographie sourcée TAM/SAM/SOM.",
    description:
      "Concurrents, segments, signaux faibles. Tavily + Perplexity Sonar pour la recherche, Cohere pour le rerank multilingue.",
    priceLabel: "Bientôt",
    defaultPrice: 149,
    status: "soon",
    cta: { label: "Donner mon avis →" },
  },
  {
    key: "financial_plan",
    name: "Plan financier",
    tagline: "Modèle 3-statement + DCF.",
    description:
      "P&L, bilan, cashflow sur 3 ans + DCF + sensibilités. Excel modifiable, hypothèses tracées.",
    priceLabel: "Bientôt",
    defaultPrice: 199,
    status: "soon",
    cta: { label: "Donner mon avis →" },
  },
];

function PhaseProPage() {
  const [feedback, setFeedback] = useState<FeedbackProductInfo | null>(null);

  return (
    <div className="ql-root" data-testid="phase-pro-root">
      {/* TOPBAR minimal */}
      <header className="ql-topbar">
        <div className="ql-wrap ql-topbar-inner">
          <QorumLogo size="sm" as="a" href="/" ariaLabel="Hōjin — accueil" />
          <nav className="ql-topbar-nav">
            <Link to="/" style={{ color: "var(--charcoal)" }}>
              ← Retour
            </Link>
            <Link to="/signup" className="ql-topbar-cta">
              Saisir le Conseil
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="ql-section-pad" aria-labelledby="phase-pro-title">
        <div className="ql-wrap" style={{ maxWidth: 880 }}>
          <div
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: "var(--gold-deep)",
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 32,
                height: 2,
                background: "var(--terracotta)",
              }}
            />
            § La Phase Pro
          </div>
          <h1
            id="phase-pro-title"
            style={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 700,
              fontSize: "clamp(36px, 5vw, 48px)",
              lineHeight: 1.1,
              color: "var(--leather)",
              margin: "0 0 18px",
            }}
          >
            Le Bureau — six livrables.
          </h1>
          <p
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontStyle: "italic",
              fontSize: 22,
              color: "var(--graphite)",
              margin: "0 0 24px",
            }}
          >
            Six livrables documentaires, par Hōjin.
          </p>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 17,
              lineHeight: 1.6,
              color: "var(--charcoal)",
              margin: "0 0 28px",
              maxWidth: 720,
            }}
          >
            Quand le Conseil a délibéré, le verdict est rendu — mais le travail
            commence. La Phase Pro réunit six livrables que Hōjin prépare pour
            transformer un débat en action : un mémo qu'on transmet, un pitch
            qu'on présente, un business plan qu'on dépose, une étude de marché
            qu'on défend. Tarif unique par livrable, pas d'abonnement, livraison
            en 60 secondes.
          </p>

          <div
            role="note"
            style={{
              background: "rgba(245,237,216,0.5)",
              border: "0.5px solid var(--accent-cramoisi)",
              padding: "14px 18px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 14,
              color: "var(--charcoal)",
            }}
          >
            🔬 Vous êtes en phase bêta. Vos votes et avis prioriseront ce qui
            sera développé en premier.
          </div>
        </div>
      </section>

      {/* GRID PRODUITS */}
      <section className="ql-section-pad" style={{ paddingTop: 0 }} aria-label="Livrables Phase Pro">
        <div className="ql-wrap">
          <div
            data-testid="phase-pro-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 24,
            }}
          >
            {PRODUCTS.map((p) => (
              <ProductCard
                key={p.key}
                product={p}
                onFeedback={() =>
                  setFeedback({
                    key: p.key,
                    name: p.name,
                    defaultPrice: p.defaultPrice,
                  })
                }
              />
            ))}
          </div>
        </div>
      </section>

      <footer className="ql-section-pad" style={{ paddingTop: 0, paddingBottom: 60 }}>
        <div className="ql-wrap" style={{ borderTop: "0.5px solid var(--stone-mid)", paddingTop: 24, display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <Link to="/comment-ca-marche" style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "var(--leather)", textDecoration: "none" }}>
            Comment ça marche →
          </Link>
          <Link to="/" style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "var(--smoke)", textDecoration: "none" }}>
            ← Accueil
          </Link>
        </div>
      </footer>

      {feedback ? (
        <FeedbackProductModal product={feedback} onClose={() => setFeedback(null)} />
      ) : null}
    </div>
  );
}

function ProductCard({
  product,
  onFeedback,
}: {
  product: Product;
  onFeedback: () => void;
}) {
  const available = product.status === "available";
  return (
    <article
      data-testid={`product-card-${product.key}`}
      data-status={product.status}
      style={{
        background: "var(--paper)",
        border: "0.5px solid var(--stone-mid)",
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        boxShadow: available
          ? "6px 6px 0 var(--leather)"
          : "4px 4px 0 var(--stone-mid)",
      }}
    >
      <span
        data-testid={`badge-${product.key}`}
        style={{
          alignSelf: "flex-start",
          fontFamily: '"DM Mono", monospace',
          fontSize: 10,
          letterSpacing: 2,
          textTransform: "uppercase",
          padding: "4px 8px",
          background: available ? "#1f5e3a" : "var(--accent-cramoisi)",
          color: available ? "var(--ivory)" : "var(--charcoal)",
          fontWeight: 700,
        }}
      >
        {available ? "Disponible — Bêta" : "Bientôt"}
      </span>

      <h3
        style={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: 24,
          color: "var(--leather)",
          margin: 0,
          lineHeight: 1.15,
        }}
      >
        {product.name}
      </h3>

      <p
        style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontStyle: "italic",
          fontSize: 16,
          color: "var(--smoke)",
          margin: 0,
        }}
      >
        {product.tagline}
      </p>

      <p
        style={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: 14,
          lineHeight: 1.55,
          color: "var(--charcoal)",
          margin: 0,
          flex: 1,
        }}
      >
        {product.description}
      </p>

      <div
        style={{
          fontFamily: '"DM Mono", monospace',
          fontSize: 11,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          color: "var(--gold-deep)",
          marginTop: 4,
        }}
      >
        {product.priceLabel}
      </div>

      {available ? (
        <Link
          to={product.cta.href ?? "/"}
          data-testid={`cta-${product.key}`}
          style={{
            display: "inline-block",
            textAlign: "center",
            marginTop: 8,
            fontFamily: '"DM Mono", monospace',
            fontSize: 11,
            letterSpacing: 2,
            textTransform: "uppercase",
            padding: "12px 18px",
            background: "var(--leather)",
            color: "var(--ivory)",
            border: "1px solid var(--leather)",
            textDecoration: "none",
            fontWeight: 700,
          }}
        >
          {product.cta.label}
        </Link>
      ) : (
        <button
          type="button"
          onClick={onFeedback}
          data-testid={`cta-${product.key}`}
          style={{
            marginTop: 8,
            fontFamily: '"DM Mono", monospace',
            fontSize: 11,
            letterSpacing: 2,
            textTransform: "uppercase",
            padding: "12px 18px",
            background: "transparent",
            color: "var(--leather)",
            border: "1px solid var(--leather)",
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          {product.cta.label}
        </button>
      )}
    </article>
  );
}
